import React from 'react';

import { T } from 'react-translator-component';
import styled from 'styled-components';

const Box = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 12.5px;
`;

const Detail = styled.p`
  margin: 6px 0 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #b02525;
  word-break: break-word;
`;

export type MapErrorBoundaryProps = {
  children: React.ReactNode;
};

type MapErrorBoundaryState = {
  message: string | null;
};

/**
 * Isola o mapa do resto da tela (MV2-1706).
 *
 * O HeatmapLayer e o Marker do @react-google-maps/api criam objetos do Google dentro de
 * useEffect. Quando essa criação falha, quem lança é o script do Google — e uma exceção
 * em efeito, sem barreira, desmonta a árvore inteira: a página fica branca, sem nem o
 * menu, e a única saída é recarregar. Foi o que aconteceu ao ligar o mapa de calor.
 *
 * Com a barreira, o pior caso é o mapa não aparecer. O filtro, a lista e o resto do
 * dashboard continuam de pé.
 *
 * A mensagem do erro vai para a tela porque erro de script de outra origem chega ao React
 * sem detalhe ("A cross-origin error was thrown"): o que der para mostrar, mostra —
 * é o que permite alguém dizer o que viu sem abrir o console.
 */
export class MapErrorBoundary extends React.Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
  constructor(props: MapErrorBoundaryProps) {
    super(props);

    this.state = { message: null };
  }

  static getDerivedStateFromError(error: unknown): MapErrorBoundaryState {
    return { message: (error as Error)?.message || 'erro sem mensagem' };
  }

  componentDidCatch(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error('Falha ao montar o mapa da aba Estações:', error);
  }

  render(): React.ReactNode {
    const { message } = this.state;
    const { children } = this.props;

    if (message === null) {
      return children;
    }

    return (
      <Box>
        {T('The map could not be drawn. The rest of the screen keeps working.')}
        <Detail>{message}</Detail>
      </Box>
    );
  }
}

export default MapErrorBoundary;
